/**
 * GuidedNewPatientForm — inline patient creation form used inside the
 * GuidedIntakeModal "New patient" step. Mirrors the field set of
 * PatientFormDrawer (basic info, contact, address) but renders inline so
 * the practitioner stays inside the enrollment wizard instead of opening
 * a side drawer.
 */

import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { State } from '@/models/state.model';
import { stateService } from '@/services/stateService';
import { patientService } from '@/services/patientService';
import { useToast } from '@/hooks/use-toast';
import { guidedNewPatientSchema } from '@/validators/patient.schema';
import { AddressData } from '@/components/ui/AddressInput';
import { PatientFormData } from './patientForm.types';
import BasicPatientInfoSection from './PatientFormBasicInfoSection';
import ContactInformationSection from './PatientFormContactInformationSection';
import AddressSection from './PatientFormAddressSection';

interface GuidedNewPatientFormProps {
  clinicId: string;
  onCreated: (patient: { id: string; first_name?: string; last_name?: string; full_name?: string; email?: string; phone?: string }) => void;
  onCancel?: () => void;
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'First Name',
  middle_name: 'Middle Name',
  last_name: 'Last Name',
  gender: 'Gender',
  dob: 'Date of Birth',
  contacts: 'Contact Information',
  addresses: 'Address',
};

const createContactId = () => {
  if (typeof globalThis.crypto !== 'undefined' && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }
  return `contact_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const createContact = (key: string) => ({
  id: createContactId(),
  key,
  value: '',
  is_default: false,
});

const GuidedNewPatientForm: React.FC<GuidedNewPatientFormProps> = ({
  clinicId,
  onCreated,
  onCancel,
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [states, setStates] = useState<State[]>([]);

  // Validation specific to the guided enrollment "New patient" form:
  // only the patient's email is required. Everything else is optional and
  // the patient fills in the rest when they open the intake link.
  const patientSchema = guidedNewPatientSchema;

  const getDefaultValues = (): PatientFormData => ({
    first_name: '',
    middle_name: '',
    last_name: '',
    gender: '',
    dob: '',
    species: 'human',
    animal_owner_name: '',
    weight: '',
    weight_recorded_date: '',
    height: undefined,
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    blood_pressure_recorded_at: '',
    medication_allergies: [],
    no_known_allergies: true,
    medical_history: '',
    practitioner_id: '',
    assignedClinic: clinicId,
    status: 'active',
    note: '',
    preferred_contact_methods: { email: true, sms: false, phone: false },
    emergency_contact_phone: '',
    insurance_carrier: '',
    insurance_member_id: '',
    insurance_group_id: '',
    contacts: [createContact('email'), createContact('phone')],
    addresses: [
      {
        addressLine1: '',
        addressLine2: '',
        state: '',
        city: '',
        postalCode: '',
        address_type: 'billing' as const,
      },
    ],
    shippingAddressEnabled: false,
    shippingSameAsBilling: true,
  });

  const form = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: getDefaultValues(),
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'contacts',
    keyName: 'fieldId',
  });

  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({
    control: form.control,
    name: 'addresses',
  });

  const handleAddressChange = (index: number) => (addressData: AddressData) => {
    form.setValue(`addresses.${index}.addressLine1`, addressData.addressLine1, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    form.setValue(`addresses.${index}.addressLine2`, addressData.addressLine2 || '', {
      shouldValidate: false,
      shouldDirty: true,
    });
    form.setValue(`addresses.${index}.city`, addressData.city, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    form.setValue(`addresses.${index}.state`, addressData.state, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    form.setValue(`addresses.${index}.postalCode`, addressData.postalCode || '', {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  useEffect(() => {
    if (states.length === 0) {
      stateService.getAllStates().then(result => {
        setStates(result.data?.states || []);
      });
    }
  }, [states]);

  const extractErrorMessage = (err: unknown, fallback: string): string => {
    const e = err as
      | {
          message?: string;
          response?: { data?: { message?: string; error?: { message?: string } } };
          data?: { message?: string; error?: { message?: string } };
          error?: { message?: string };
        }
      | undefined;
    return (
      e?.response?.data?.error?.message ||
      e?.response?.data?.message ||
      e?.data?.error?.message ||
      e?.data?.message ||
      e?.error?.message ||
      e?.message ||
      fallback
    );
  };

  const handleSubmit = async (data: PatientFormData) => {
    setIsLoading(true);
    try {
      // The guided-patient endpoint only requires an email contact; the
      // backend fills placeholder values for any missing fields that the
      // patient will overwrite when they complete the intake form.
      const result = await patientService.createGuidedPatient(clinicId, data);
      if (result?.success && result?.data) {
        const created = result.data as {
          id: string;
          first_name?: string;
          last_name?: string;
          full_name?: string;
          email?: string;
          phone?: string;
        };
        toast({
          title: 'Patient added',
          description: 'Continuing with enrollment.',
          variant: 'success',
        });
        onCreated(created);
        form.reset(getDefaultValues());
        return;
      }
      // Server returned 200 but success=false (e.g. duplicate patient).
      // If a duplicate matched, advance using the existing patient instead of
      // forcing the practitioner to start over.
      const existingId = (result as { data?: { existingPatientId?: string } } | undefined)?.data
        ?.existingPatientId;
      if (existingId) {
        const emailContact = (data?.contacts || []).find(c => c.key === 'email');
        toast({
          title: 'Existing patient matched',
          description: 'A patient with this email already exists. Continuing with that record.',
          variant: 'default',
        });
        onCreated({ id: existingId, email: emailContact?.value });
        return;
      }
      toast({
        title: "Couldn't add patient",
        description: extractErrorMessage(result, 'Patient creation failed. Please try again.'),
        variant: 'destructive',
      });
    } catch (error) {
      toast({
        title: "Couldn't add patient",
        description: extractErrorMessage(error, 'Patient creation failed. Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidationError = (errors: Record<string, unknown>) => {
    const errorKeys = Object.keys(errors);
    const fieldNames = errorKeys.map(k => FIELD_LABELS[k] || k).join(', ');
    toast({
      title: 'Missing required fields',
      description: `Please complete: ${fieldNames}`,
      variant: 'destructive',
    });
    setTimeout(() => {
      const firstError = document.querySelector('.text-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 150);
  };

  return (
    <Form {...form}>
      <form
        id="guided-new-patient-form"
        onSubmit={form.handleSubmit(handleSubmit, handleValidationError)}
        className="space-y-6"
      >
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
          <div className="space-y-0.5 text-sm">
            <p className="font-medium text-blue-900">
              Required: first name, last name, gender, email, and phone
            </p>
            <p className="text-xs text-blue-800/80">
              Anything else you leave blank — middle name, date of birth, address — the
              patient fills in when they open the intake link.
            </p>
          </div>
        </div>

        <BasicPatientInfoSection
          form={form}
          isGuided={true}
          requiredFields={['first_name', 'last_name', 'gender']}
        />

        <ContactInformationSection
          form={form}
          fields={fields}
          append={append}
          remove={remove}
          createContact={createContact}
          isGuided={true}
        />

        <AddressSection
          form={form}
          addressFields={addressFields}
          states={states}
          handleAddressChange={handleAddressChange}
          appendAddress={appendAddress}
          removeAddress={removeAddress}
          hideRequiredMarkers
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          {onCancel && (
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            disabled={isLoading}
            className="h-10 gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Add patient & continue'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default GuidedNewPatientForm;
